import { Card } from "react-bootstrap";
import * as React from 'react';
import { translateService, Translate } from "../../services/TranslateService";
import styles from "./VehicleSearch.module.scss";
import CustomTooltip from "../CustomTooltip";
import VehicleDetails from "./VehicleDetails";
import { useSelector } from "react-redux";
import { baseOnSalesPrice, basePrice } from "../MakeReservation/pricingCalculations";
import { cx, priceFormat } from "../MakeReservation/util";
import {GlobalStore} from "../../reduxStore";
import {VehicleImageSwiper} from "./VehicleImageSwiper";
import {useContext} from "react";
import {DataContext} from "../../contexts/dataContext";
import {Rating} from "@mui/material";

type SetDetailsVisibleFunctionI = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
interface VehicleResultProps {
    vehicle: any;
    selectVehicle: Function;
    setRedirect: Function | null;
    totalPrice?: number;
    detailsVisible:boolean;
    setDetailsVisible: SetDetailsVisibleFunctionI;
    discount?: null |  number;
    noVat: boolean;
    /** First card of the initial viewport — see VehicleImageSwiper.priorityImage. */
    priorityImage?: boolean;
}

interface PriceDisplayProps {
    totalPrice: number;
    noVatPrices: boolean;
    changeNoVatPrices: Function;
}

export const PriceDisplay = ({totalPrice, noVatPrices, changeNoVatPrices}:PriceDisplayProps) => {
    return <>
        <div className={cx('flex-column align-items-center', 'd-flex justify-content-around')}>
            <div className={styles.priceH4}>
                {priceFormat(totalPrice)+' € '}
            </div>
            <div className={cx(styles.includeVat, styles.includeVatClickable)}
                 onClick={() => {changeNoVatPrices()}}>
                <Translate
                    with={{type: noVatPrices ? translateService.translate('vehicleSearch.vatPercent0')
                            : translateService.translate('vehicleSearch.vatPercent25')}}
                    content='vehicleSearch.vatIncluded'/>
                <span className={styles.vatChangeIcon}>⇄</span>
            </div>
        </div>
    </>
}

const DefaultVehicleResult = (({ noVat, vehicle, selectVehicle, totalPrice = vehicle.onsale ? baseOnSalesPrice(vehicle, noVat) : basePrice(vehicle, noVat), detailsVisible,
                            setDetailsVisible, setRedirect=null, priorityImage=false}: VehicleResultProps) => {
    const dataContext = useContext(DataContext);
    const language = useSelector((state: GlobalStore) => state.chauffeurReducer.deviceLanguage);
    let price = totalPrice;

    if ((vehicle.vehicleModelData.ageLimit ?? 18 ) > 18) {
        vehicle.vehicleData.noundertwentyone = true;
    } else {
        vehicle.vehicleData.noundertwentyone = false;
    }

    let fuelType: string;
    if (+vehicle.vehicleData.fuelType === 0)
        fuelType = translateService.translate('vehicleSearch.diesel');
    else if (+vehicle.vehicleData.fuelType === 1)
        fuelType = translateService.translate('vehicleSearch.95E');
    else
        fuelType = translateService.translate('vehicleSearch.electric');

    if (vehicle.vehicleData.hybridity === 'normal')
        fuelType += " - "+ translateService.translate('vehicleSearch.hybrid');
    else if (vehicle.vehicleData.hybridity !== 'none')
        fuelType += " - "+ translateService.translate('vehicleSearch.plugin');

    const changeTwentyModal = () => {
        dataContext.changeTwentyModal(true)
        if(dataContext.twentyModal){
            dataContext.changeTwentyModal(false)
        }
    }

    return <>
        <div className={'w-100'}>
            <Card className={`card-body my-3 p-3`}>
                <div className={'d-flex flex-lg-row flex-md-row flex-column'}>
                    <div className={`col-12 col-md-4 col-lg-4 px-0`} style={{height: "170px"}}>
                        <VehicleImageSwiper vehicle={vehicle} priorityImage={priorityImage}/>
                    </div>
                    <div className={'col-12 col-md-8 col-lg-8 mt-sm-2 pl-lg-2 pl-md-2 px-0 '}>
                        <div className={`${styles.carModel} d-flex align-items-center justify-content-between flex-row`}>
                            <h2>
                                {vehicle.vehicleModelData.manufacturer} {vehicle.vehicleModelData.name} {vehicle.vehicleModelData.dimensions.trunkVolume !== 0 ?
                                vehicle.vehicleModelData.dimensions.trunkVolume + ' m³' : ''}
                            </h2>
                            <div>
                                <CustomTooltip className="tooltip-link" zIndex={2}
                                       content={translateService.translate('vehicleSearch1.starsTooltip')}>
                                    <div className={'d-flex pr-1'}>
                                        <Rating name="read-only" max={1} value={5} readOnly />
                                        <span>5</span>
                                    </div>
                                </CustomTooltip >
                            </div>
                        </div>
                        <div className={'d-flex flex-column-reverse flex-md-row-reverse flex-lg-row-reverse align-content-center align-items-center justify-content-between'}>
                            <div className={'align-self-baseline banner-label col-12 col-md-4 col-lg-4 px-0'}>
                                <div className={`${cx(styles.r24ChildFlex1)} m-0`} ng-show="model.onSalesPrice == 0">
                                    <div className={cx(styles.carPriceGreenBackground)}>
                                        <PriceDisplay totalPrice={price}
                                                      noVatPrices={dataContext.noVatPrices}
                                                      changeNoVatPrices={dataContext.changeNoVatPrices}/>
                                    </div>
                                </div>
                            </div>
                            <div className={'pr-2 pl-0 col-12 col-md-8 col-lg-8'}>
                                <div className={styles.carModelNeighborhood}>{vehicle.homeLocationData.neighborhood}</div>
                                <div className={styles.infoContainer}>
                                   <div className={styles.addressContainer}>
                                                {/* One-way res not featured in default results so relate display has been omitted */}
                                                <span className='d-flex flex-row'>
                                                    <img width="23" height="23" className={`${styles.carFeatureIcon} `}
                                                         src={dataContext.rootUrl + '/assets/icons/new/map-pin-icon.svg'}
                                                         alt="ap-pin-icon" loading="lazy"/>
                                                    <div className={'text-wrap'}>
                                                        <span className={styles.carAddress}> {!dataContext.outOfRange ? vehicle.homeLocationData.fullAddress?.replace(vehicle.homeLocationData.postalCode ?? '', '') :
                                                            vehicle.homeLocationData.neighborhood + ' ' + vehicle.homeLocationData.city} <b>{Math.round((+vehicle.distance / 1000) * 10) / 10} km</b></span>
                                                    </div>
                                                </span>
                                        </div>
                                    <div>
                                        {vehicle.parkingPermit.valid &&
                                            <>
                                                <div className={styles.r24Parking}>
                                                    <img width="23" height="23" className={styles.carFeatureIcon}
                                                         src={dataContext.rootUrl + "/assets/icons/new/search/icon-z-parking-permit.svg"}
                                                         alt="icon-z-parking-permit" loading="lazy"/>
                                                    <span className={styles.r24ParkingTxt}> <Translate content={'vehicleSearch.freeParking'}/>{' '}{vehicle.vehicleData.parkingPermitCityName}</span>
                                                    <a className={`btn btn-sm btn-outline-info ${styles.greenReadMoreButton}`} target="_blank"
                                                       rel="noopener noreferrer"
                                                       style={{fontSize: '10px'}}
                                                       href={language === 'fi' ? "https://www.24rent.fi/pysakointi-z-luvalla/" :
                                                           "https://www.24rent.fi/en/parking-with-parking-permit-z/"}>
                                                        <strong><Translate content={'vehicleSearch.readMore'}/>...</strong>
                                                    </a>
                                                </div>
                                            </>}
                                    </div>
                                    <div>
                                        <div className={styles.iconAndText}>
                                            <CustomTooltip className="tooltip-link" zIndex={2}
                                                content={<Translate content={'vehicleSearch.numberOfSeats'}/>}>
                                                <img width="23" height="23" className={styles.carFeatureIcon}
                                                     src={dataContext.rootUrl + "/assets/icons/new/car-feature-icons/icon-car-feature-seats.svg"}
                                                     alt="icon-car-feature-seats" loading="lazy"/>
                                            </CustomTooltip>
                                            <span className={styles.baseIconMargin}>{vehicle.vehicleData.seatCount}</span>
                                        </div>
                                        <div className={styles.iconAndText}>
                                            <CustomTooltip className="tooltip-link" zIndex={2}
                                                content={<Translate content={'welcomePhase.vehicleInfoModal.transmission'}/>}>
                                                <img width="23" height="23" className={styles.carFeatureIcon}
                                                     src={dataContext.rootUrl + "/assets/icons/new/car-feature-icons/icon-car-feature-gears.svg"}
                                                     alt="icon-car-feature-gears" loading="lazy"/>
                                            </CustomTooltip>
                                            <span className={styles.baseIconMargin}>
                                                {!vehicle.vehicleData.hasAutomaticTransmission ? <Translate content={'vehicleSearch.manual'}/> :
                                                    <Translate content={'vehicleSearch.automatic'}/>}
                                            </span>
                                        </div>
                                        <div className={styles.iconAndText}>
                                            <CustomTooltip className="tooltip-link" zIndex={2}
                                                content={<Translate content={'welcomePhase.vehicleInfoModal.fuel'}/>}>
                                                <img width="23" height="23" className={styles.carFeatureIcon}
                                                     src={dataContext.rootUrl + "/assets/icons/new/car-feature-icons/icon-car-feature-fuel.svg"}
                                                     alt="icon-car-feature-fuel" loading="lazy"/>
                                            </CustomTooltip>
                                            <span className={styles.baseIconMargin}>
                                                {fuelType}
                                                {(vehicle.vehicleData.hybridity === 'normal' || vehicle.vehicleData.hybridity === 'plug-in' || +vehicle.vehicleData.fuelType === 2) &&
                                                    <span className={styles.r24EcoText}> ECO</span>}
                                            </span>
                                        </div>
                                        {vehicle.vehicleData.hasHandtruck &&
                                            <div className={styles.iconAndText}>
                                                <CustomTooltip className="tooltip-link" zIndex={2}
                                                    content={<Translate content={'vehicleSearch.handtruck'}/>}>
                                                    <img width="23" height="23" className={styles.carFeatureIcon}
                                                         src={dataContext.rootUrl + "/assets/icons/new/car-feature-icons/icon-car-feature-handtruck.svg"}
                                                         alt="icon-car-feature-handtruck" loading="lazy"/>
                                                </CustomTooltip>
                                                <span className={styles.baseIconMargin}> <Translate
                                                    content={'vehicleSearch.handtruck'}/> </span>
                                            </div>
                                        }
                                        {vehicle.vehicleData.hasHitch &&
                                            <div className={styles.iconAndText}>
                                                <CustomTooltip className="tooltip-link" zIndex={2}
                                                    content={<Translate content={'vehicleSearch.towingHitch'}/>}>
                                                    <img width="23" height="23" className={styles.carFeatureIcon}
                                                         src={dataContext.rootUrl + "/assets/icons/new/car-feature-icons/icon-car-feature-towbar.svg"}
                                                         alt="icon-car-feature-towbar" loading="lazy"/>
                                                </CustomTooltip>
                                                <span className={styles.baseIconMargin}> {vehicle.vehicleData.hitchPinCount} - <Translate
                                                    content={'vehicleSearch.pins'}/></span>
                                            </div>
                                        }
                                        {vehicle.vehicleData.noundertwentyone ?
                                            <>
                                                <div className={styles.iconAndTextUnder21} onClick={() => changeTwentyModal()}>
                                                    <CustomTooltip className="tooltip-link" zIndex={2}
                                                        content={<Translate content={'vehicleSearch.noundertwentyone'}/>}>
                                                        <img width="35" height="35" style={{width: '35px', height: '35px'}}
                                                             src={dataContext.rootUrl + "/assets/icons/new/car-feature-icons/icon-car-feature-noundertwntyone.svg"}
                                                             alt={translateService.translate('vehicleSearch.noundertwentyone')} loading="lazy"/>
                                                    </CustomTooltip>
                                                </div>
                                            </> : null
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <VehicleDetails vehicle={vehicle} detailsVisible={detailsVisible} setDetailsVisible={setDetailsVisible}>
                    {setRedirect &&
                        <button onClick={() => {selectVehicle(vehicle)}}
                                id={'defaultVehicleResultSelectButton'}
                                className="btn btn-primary btn-outline-primary btn-all mt-2 w-100 align-self-end
                                rounded-3 font-weight-bold">
                            <Translate content={'vehicleSearch1.selectLocationAndDate'} />
                        </button>
                    }
                </VehicleDetails>
            </Card>
        </div>
    </>
});


export default DefaultVehicleResult;